import { formatUtcOffset } from '@felix/domain';
import {
  ActionIcon,
  Anchor,
  Button,
  Popover,
  RingProgress,
  Select,
  Skeleton,
  Tabs,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconAlertTriangle,
  IconBed,
  IconBriefcase,
  IconCalendar,
  IconCertificate,
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconCircleCheckFilled,
  IconExclamationMark,
  IconInfoCircleFilled,
  IconNotes,
  IconPencil,
  IconSearch,
  IconShipOff,
  IconUserSearch,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime, formatDay, formatDayLong } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { PersonAvatar } from '../../PersonAvatar.js';
import { StatTile } from '../../StatTile.js';
import { StatusBadge } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import { TOKENS } from '../../tokens.js';
import { VesselCard } from '../../VesselCard.js';
import { ClockDayBand } from './ClockDayBand.js';
import { queryUser } from './CrewListScreen.js';
import { PeriodNav } from './PeriodNav.js';
import { RestDayEditor } from './RestDayEditor.js';
import classes from './RestHoursScreen.module.css';
import { RestMonthView } from './RestMonthView.js';
import {
  acceptDate,
  activityTotals,
  complianceNotice,
  dayOf,
  daySegments,
  formatClock,
  formatHours,
  formatMinutes,
  loadRestReport,
  MLC_LIMITS,
  type RestReportData,
  restMeter,
  restRoster,
  shiftDate,
  statusHere,
} from './rest-hours.js';
import {
  ActivityDot,
  colorOf,
  DAY_ICON,
  DAY_TONE,
  EMPTY,
  IconUsersFilled,
  RestNotice,
  restTable,
} from './rest-ui.js';
import {
  type ClockChangeView,
  clockText,
  dayAxis,
  daySpan,
  isPlainDay,
  offsetAt,
  type ShipTime,
  shipClockOf,
  wallClock,
} from './ship-clock.js';
import { errorCode, type PersonnelSource } from './source.js';
import type {
  CrewMember,
  CrewRestReport,
  PersonnelVessel,
  RestMonth,
  WorkPeriod,
} from './types.js';

type Status = CrewRestReport['days'][number]['status'];

/** Yükleniyor iskeletindeki satır sayısı (G3-18). */
const SKELETON_ROWS = 5;

/** Kişi kartının sekmeleri (personel-mlc-calisma-dinlenme.jpeg sırasıyla). */
const TAB_KEYS = ['daily', 'weekly', 'monthly', 'certificates', 'notes'] as const;

type Base =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; today: string; vessels: PersonnelVessel[]; crew: CrewMember[] };

type Report =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | ({ kind: 'ready' } & RestReportData);

const NO_CREW: readonly CrewRestReport[] = [];

export interface RestHoursScreenProps {
  source: PersonnelSource;
}

const clock = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

/**
 * Çalışma ve dinlenme saatleri (müşteri ekranı `docs/ekranlar/personel-mlc-calisma-dinlenme.jpeg`;
 * ADR 0016–0018, ADR 0019). Hesap sunucuda, gemide ve bulutta aynı fonksiyonla; ekran sonucu
 * gösterir. Saat girişi ve kaptan onayı gemide (M10d; MLC A2.3 §12). Personel listesi rütbe
 * kıdemiyle (M6f G3-17); `?user=<kimlik>` ile gelince o kişi seçilir. Listede seçili gemi gününde
 * bu gemide ataması olanlar görünür; ayrılan kişi ve ayları tarih seçiciyle geçmiş günden açılır
 * (ortak notu P1, 2026-11-02; ADR 0016 Ek).
 */
/**
 * Sağ sütunda "Active Alerts" kartı yok: sertifika uyarıları Certificates'ta gösterilir (ortağın
 * geri bildirimi 2026-10-17; ADR 0068 §7). Gemi ve ofis ekranı aynı.
 */
export function RestHoursScreen({ source }: RestHoursScreenProps) {
  const { t, i18n } = useTranslation();
  const shell = useShell();
  const locale = i18n.resolvedLanguage ?? 'en';
  const focusUser = queryUser(shell?.search);
  const compact = useMediaQuery('(max-width: 1439px)') ?? false;
  // Sağ sütun 1600 altında daralır: uyum halkası küçülür
  const narrowSide = useMediaQuery('(max-width: 1599px)') ?? false;
  const [base, setBase] = useState<Base>({ kind: 'loading' });
  const [report, setReport] = useState<Report>({ kind: 'idle' });
  const [periods, setPeriods] = useState<WorkPeriod[] | null>(null);
  const [periodsError, setPeriodsError] = useState<string | null>(null);
  const [vesselId, setVesselId] = useState<string | null>(null);
  const [date, setDate] = useState<string | null>(null);
  // Aylık sekmenin ayı ekranda tutulur: kişi değişince korunur (G3-23)
  const [month, setMonth] = useState<string | null>(null);
  const [personId, setPersonId] = useState<string | null>(focusUser);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<string | null>('daily');
  // Gemide dönem girişi (M10d2): yazmadan sonra rapor ve dönemler yeniden okunur
  const [editing, setEditing] = useState(false);
  const [revision, setRevision] = useState(0);
  const [monthRecord, setMonthRecord] = useState<RestMonth | null>(null);
  const editButton = useRef<HTMLButtonElement>(null);

  const loadBase = useCallback(() => {
    setBase({ kind: 'loading' });
    Promise.all([source.vessels(), source.crew()])
      .then(([vessels, crew]) =>
        setBase({ kind: 'ready', today: crew.today, vessels, crew: crew.crew }),
      )
      .catch((error: unknown) => setBase({ kind: 'error', code: errorCode(error) }));
  }, [source]);
  useEffect(loadBase, [loadBase]);

  // Bildirimden başka kişiye gelince (`?user=`) o kişi seçilir
  useEffect(() => {
    if (focusUser) setPersonId(focusUser);
  }, [focusUser]);

  const ready = base.kind === 'ready' ? base : null;
  const today = ready?.today ?? null;
  const day = date ?? today;
  const vessels = useMemo(
    () => [...(ready?.vessels ?? [])].sort((x, y) => x.name.localeCompare(y.name, locale)),
    [ready, locale],
  );
  // `?user=` ile gelen kişinin gemisi (ofiste filo); yoksa ilk gemi
  const focusVessel = ready?.crew.find((member) => member.userId === focusUser)?.vessel?.id;
  const activeVessel =
    vessels.find((v) => v.id === vesselId) ??
    vessels.find((v) => v.id === focusVessel) ??
    vessels[0] ??
    null;
  const people = useMemo(
    () => new Map((ready?.crew ?? []).map((member) => [member.userId, member])),
    [ready],
  );

  const weekFrom = day ? shiftDate(day, -6) : null;
  // Rapor: gemi ya da gün değişince iskelet; yazmadan sonra (revision) eski veri yerinde kalır.
  // Liste üyeliği raporla birlikte gelir (ortak notu P1): yüklenirken süzülmemiş listeye dönmez
  const reportKey = activeVessel && day ? `${activeVessel.id}|${day}` : null;
  const shownReportKey = useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `revision` yazmadan sonra yeniden okutur
  const loadReport = useCallback(() => {
    if (!ready || !activeVessel || !day || !reportKey) return;
    if (shownReportKey.current !== reportKey) setReport({ kind: 'loading' });
    let cancelled = false;
    loadRestReport(source, {
      vesselId: activeVessel.id,
      day,
      today: ready.today,
      crew: ready.crew,
      // Atlanan günde tek günlük çağrı yapılmaz (ADR 0065 §4)
      clock: activeVessel.clock,
    })
      .then((result) => {
        if (cancelled) return;
        shownReportKey.current = reportKey;
        setReport({ kind: 'ready', ...result });
      })
      .catch((error: unknown) => {
        if (!cancelled) setReport({ kind: 'error', code: errorCode(error) });
      });
    return () => {
      cancelled = true;
    };
  }, [source, ready, activeVessel, day, reportKey, revision]);
  useEffect(loadReport, [loadReport]);

  // 7 günlük rapor: uyum hesabı, haftalık tablo, grafik ve ad bulma
  const crew = report.kind === 'ready' ? report.crew : NO_CREW;
  const members = report.kind === 'ready' ? report.members : null;
  const rankOf = useCallback((userId: string) => people.get(userId)?.role ?? null, [people]);
  // Liste, arama, sıralama, sayaçlar ve varsayılan kişi seçili gemi gününde bu gemide görevli
  // olanlardan (ortak notu P1); `?user=` ile gelen kişi süzgece takılmaz. Düzenleme penceresi
  // açıkken düzenlenen kişi kalır: pencerenin gün düğmeleri kişiyi değiştirmez
  const editingUser = editing ? personId : null;
  const roster = useMemo(
    () =>
      day
        ? restRoster({
            crew,
            members,
            day,
            focusUser,
            editingUser,
            personId,
            query,
            rankOf,
            locale,
          })
        : null,
    [crew, members, day, focusUser, editingUser, personId, query, rankOf, locale],
  );
  const counts = roster?.counts ?? null;
  const visibleCrew = roster?.visible ?? NO_CREW;
  const person = roster?.person ?? null;
  const member = person ? people.get(person.userId) : undefined;
  // Düzenlenen kişi gidilen günün 7 günlük raporunda yoksa (ör. katılıştan önceki gün) pencere
  // kapanır: başka bir görevli için yeniden açılmaz, saat başkasının adına girilmez
  useEffect(() => {
    if (!editing || report.kind !== 'ready' || person) return;
    setEditing(false);
    requestAnimationFrame(() => editButton.current?.focus());
  }, [editing, report.kind, person]);
  // Durum rozeti ve "On board" noktası yalnız bu geminin kaydından (ofiste `/crew` filo geneli)
  const statusOf = (userId: string) => statusHere(people.get(userId), activeVessel?.id);
  const personStatus = person ? statusOf(person.userId) : null;
  // Tarih çizgisinde iki kez yaşanan günde en kötü oluşum (ADR 0065 §4)
  const today24 = (person && day ? dayOf(person, day) : undefined) ?? null;

  // Dönemler: kişi ya da gün değişince boş başlar; yazmadan sonra eski liste kalır (G3-29)
  const personKey = person?.userId ?? null;
  const periodsKey =
    activeVessel && personKey && day ? `${activeVessel.id}|${personKey}|${day}` : null;
  const shownPeriodsKey = useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `revision` yazmadan sonra yeniden okutur
  useEffect(() => {
    if (!activeVessel || !personKey || !day || !weekFrom || !periodsKey) {
      setPeriods(null);
      shownPeriodsKey.current = null;
      return;
    }
    if (shownPeriodsKey.current !== periodsKey) setPeriods(null);
    setPeriodsError(null);
    let cancelled = false;
    source
      .workPeriods(activeVessel.id, personKey, weekFrom, day)
      .then((result) => {
        if (cancelled) return;
        shownPeriodsKey.current = periodsKey;
        setPeriods(result);
      })
      .catch((error: unknown) => {
        if (!cancelled) setPeriodsError(errorCode(error));
      });
    return () => {
      cancelled = true;
    };
  }, [source, activeVessel, personKey, weekFrom, day, periodsKey, revision]);

  // Düzenleme penceresi açıkken günün ayının durumu: imzalı ay kilitli (G3-28; ADR 0021 §5)
  // biome-ignore lint/correctness/useExhaustiveDependencies: `revision` yazmadan sonra yeniden okutur
  useEffect(() => {
    if (!editing || !source.editor || !activeVessel || !personKey || !day) {
      setMonthRecord(null);
      return;
    }
    let cancelled = false;
    source
      .restMonth(activeVessel.id, personKey, day.slice(0, 7))
      .then((record) => {
        if (!cancelled) setMonthRecord(record);
      })
      .catch(() => {
        if (!cancelled) setMonthRecord(null);
      });
    return () => {
      cancelled = true;
    };
  }, [editing, source, activeVessel, personKey, day, revision]);

  const timeZone = report.kind === 'ready' ? report.timeZone : (activeVessel?.timeZone ?? 'UTC');
  // Gemi saati: dilim ve onaylı saat değişiklikleri (ADR 0065 §4); onay bekleyen hesaba girmez
  const clockChanges = report.kind === 'ready' ? report.clockChanges : null;
  const shipTime: ShipTime = useMemo(
    () => shipClockOf(timeZone, clockChanges ?? []),
    [timeZone, clockChanges],
  );
  const span = useMemo(() => (day ? daySpan(shipTime, day) : null), [shipTime, day]);
  const axis = useMemo(() => (span ? dayAxis(span) : null), [span]);
  const plainDay = span === null || isPlainDay(span);
  const segments = useMemo(
    () => (periods && day ? daySegments(periods, shipTime, day) : []),
    [periods, shipTime, day],
  );
  const dayLength = span?.minutes ?? 1440;
  /** Gerçek dakikanın gemi saati etiketi; tekrar eden saatte "(1st)"/"(2nd)" (ADR 0065 §7). */
  const boundary = (real: number, side: 'start' | 'end' = 'start') => {
    if (plainDay || !span) return clock(real === dayLength ? 1440 : real);
    if (real >= dayLength) return '24:00';
    const at = span.start + real * 60_000;
    // Bitiş anı önceki saatle okunur: geri almada 24:00 (1.), "23:00 (2.)" değil
    const wall = wallClock(shipTime, side === 'end' ? at - 60_000 : at);
    const minute =
      Number(wall.time.slice(0, 2)) * 60 + Number(wall.time.slice(3, 5)) + (side === 'end' ? 1 : 0);
    const text = clockText(minute);
    return wall.occurrence && minute % 1440 !== 0
      ? `${text} ${t(`restHours.clock.occurrence${wall.occurrence}`)}`
      : text;
  };
  /** Görünen süre (duvar saati farkı) gerçek süreden farklıysa. */
  const shownMinutes = (from: number, to: number) => {
    if (plainDay || !span) return null;
    const pieces = span.timeline.pieces;
    // Başlangıç anı sonraki parçaya, bitiş anı önceki parçaya aittir (24:00 (1.) / 23:00 (2.))
    const start = pieces.find((p) => from >= p.from && from < p.to) ?? pieces[0];
    const end = pieces.find((p) => to > p.from && to <= p.to) ?? pieces.at(-1);
    if (!start || !end) return null;
    const shown = end.wallFrom + (to - end.from) - (start.wallFrom + (from - start.from));
    return shown === to - from ? null : shown;
  };
  // Yaklaşan ve onay bekleyen saat değişiklikleri (ADR 0065 §5)
  const clockNoticeList = useMemo(() => {
    const now = Date.now();
    return (clockChanges ?? []).filter(
      (c) =>
        c.status === 'pending' ||
        (c.status === 'approved' &&
          Date.parse(c.effectiveAt) > now &&
          Date.parse(c.effectiveAt) <= now + 48 * 3_600_000),
    );
  }, [clockChanges]);
  const clockNoticeText = (change: ClockChangeView) => {
    const wall = new Date(
      Date.parse(change.effectiveAt) + change.oldOffsetMin * 60_000,
    ).toISOString();
    const values = {
      date: formatDay(wall.slice(0, 10)),
      time: wall.slice(11, 16),
      minutes: Math.abs(change.changeMin),
      from: formatUtcOffset(change.oldOffsetMin),
      to: formatUtcOffset(change.newOffsetMin),
    };
    if (change.status === 'pending') return t('restHours.clock.pending', values);
    return t(`restHours.clock.${change.changeMin < 0 ? 'willRetard' : 'willAdvance'}`, values);
  };
  const totals = useMemo(() => activityTotals(periods ?? []), [periods]);
  const totalWork = totals.reduce((sum, item) => sum + item.minutes, 0);
  const restParts = segments.filter((s) => s.kind === 'rest' && !s.short);
  const findings = today24?.findings ?? [];
  const notice = complianceNotice(today24?.status ?? null, findings.length);
  const shownMonth = month ?? day?.slice(0, 7) ?? null;
  // Gemi saatiyle şimdi (gün bugünse bantta ▲; G3-37)
  const nowMinute =
    day && today && day === today ? Math.round((Date.now() - (span?.start ?? 0)) / 60_000) : null;

  // Tasarımdaki gibi gün önce ve kısa ay ("28 Sep"); İngilizcede en-US ay adları (en-GB "Sept")
  const monthName = useMemo(
    () =>
      new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : locale, {
        month: 'short',
        timeZone: 'UTC',
      }),
    [locale],
  );
  const asDate = (value: string) => new Date(`${value}T00:00:00Z`);
  const activityLabel = (activity: string | null) =>
    t(`restHours.activity.${activity ?? 'rest'}`, { defaultValue: activity ?? '' });
  const statusBadge = (status: Status) => {
    const Icon = DAY_ICON[status];
    return (
      <StatusBadge tone={DAY_TONE[status]} icon={<Icon size={12} stroke={2.2} />}>
        {t(`restHours.status.${status}`)}
      </StatusBadge>
    );
  };
  /** Gün değişir; ay sınırı geçilince aylık sekmenin ayı da (G3-23). */
  const changeDay = (next: string) => {
    if (!today || !acceptDate(next, today)) return;
    if (day && next.slice(0, 7) !== day.slice(0, 7)) setMonth(next.slice(0, 7));
    setDate(next);
  };
  const move = (days: number) => {
    if (day) changeDay(shiftDate(day, days));
  };
  const selectVessel = (id: string) => {
    setVesselId(id);
    setPersonId(null);
  };

  const header = (
    <PageHeader
      title={t('restHours.title')}
      description={t('restHours.subtitle')}
      aside={
        activeVessel ? (
          <VesselCard
            vessel={activeVessel}
            options={source.scope === 'web' ? vessels : undefined}
            onSelect={selectVessel}
          />
        ) : undefined
      }
    />
  );

  if (base.kind === 'error' || report.kind === 'error') {
    return (
      <div className={classes.main}>
        {header}
        <Panel>
          <EmptyState
            tone="danger"
            title={t('restHours.error')}
            description={t('common.loadError')}
            action={
              <Button
                size="xs"
                variant="light"
                onClick={base.kind === 'error' ? loadBase : () => setRevision((n) => n + 1)}
              >
                {t('common.retry')}
              </Button>
            }
          />
        </Panel>
      </div>
    );
  }

  if (ready && vessels.length === 0) {
    return (
      <div className={classes.main}>
        {header}
        <Panel>
          <EmptyState
            icon={<IconShipOff size={22} stroke={1.8} />}
            title={t('restHours.noVessels')}
          />
        </Panel>
      </div>
    );
  }

  const weekDays = person?.days ?? [];
  const ring = today24?.rest24h ?? null;
  const loadingCrew = report.kind === 'loading' || base.kind === 'loading';
  const meter = restMeter(today24?.rest7d ?? null);
  const compliant = ring !== null && ring >= MLC_LIMITS.minRest24h;

  const dailyRows =
    periods === null ? (
      periodsError ? (
        <tr>
          <td colSpan={6}>
            <EmptyState
              tone="danger"
              title={t('restHours.periodsError')}
              action={
                <Button size="xs" variant="light" onClick={() => setRevision((n) => n + 1)}>
                  {t('common.retry')}
                </Button>
              }
            />
          </td>
        </tr>
      ) : (
        Array.from({ length: SKELETON_ROWS }, (_, n) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: sabit iskelet satırları
          <tr key={n} aria-hidden>
            {[40, 40, 34, 110, 60, 90].map((width, m) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: sabit iskelet hücreleri
              <td key={m}>
                <Skeleton height={10} width={width} />
              </td>
            ))}
          </tr>
        ))
      )
    ) : (
      segments.map((segment) => {
        const activity = segment.kind === 'work' ? segment.activity : null;
        return (
          <tr
            key={`${segment.from}-${segment.to}`}
            className={segment.kind === 'rest' ? restTable.restRow : undefined}
          >
            <td className={restTable.num}>{boundary(segment.from)}</td>
            <td className={restTable.num}>{boundary(segment.to, 'end')}</td>
            <td className={restTable.num}>
              {(() => {
                // Saat değişen günde görünen süre gerçek süreden farklı olabilir (ADR 0065 §7)
                const shown = shownMinutes(segment.from, segment.to);
                const actual = formatMinutes(segment.to - segment.from);
                return shown === null ? (
                  actual
                ) : (
                  <>
                    {t('restHours.clock.actual', { value: actual })}
                    <small className={classes.actualTime}>
                      {t('restHours.clock.shown', { value: formatMinutes(shown) })}
                    </small>
                  </>
                );
              })()}
            </td>
            <td className={classes.activityCell}>
              <span className={restTable.activity}>
                <ActivityDot activity={activity} />
                {activityLabel(activity)}
                {segment.short ? (
                  <small className={classes.short}>{t('restHours.shortBreak')}</small>
                ) : null}
              </span>
            </td>
            <td className={restTable.secondary}>{segment.location ?? EMPTY}</td>
            <td className={`${restTable.secondary} ${classes.noteCell}`}>
              {segment.note ?? EMPTY}
            </td>
          </tr>
        );
      })
    );

  return (
    <div className={classes.screen}>
      <div className={classes.main}>
        {header}

        <section
          className={classes.tiles}
          aria-label={t('restHours.summary.label', { date: day ? formatDay(day) : '' })}
        >
          {counts && report.kind === 'ready' ? (
            <>
              <StatTile
                icon={<IconUsersFilled size={26} />}
                label={t('restHours.summary.total')}
                value={counts.total}
              />
              <StatTile
                icon={<IconCheck size={24} stroke={3} />}
                label={t('restHours.summary.compliant')}
                value={counts.compliant}
                tone="success"
              />
              <StatTile
                icon={<IconAlertTriangle size={22} stroke={2.2} />}
                label={t('restHours.summary.warning')}
                value={counts.warning}
                tone="warning"
              />
              <StatTile
                icon={<IconExclamationMark size={24} stroke={3} />}
                label={t('restHours.summary.violation')}
                value={counts.violation}
                tone="danger"
              />
            </>
          ) : (
            [0, 1, 2, 3].map((n) => <Skeleton key={n} height={84} radius="md" />)
          )}
        </section>

        <div className={classes.body}>
          <Panel title={t('restHours.crewList')} className={classes.crewPanel}>
            <Select
              label={t('restHours.vessel')}
              data={vessels.map((v) => ({ value: v.id, label: v.name }))}
              value={activeVessel?.id ?? null}
              allowDeselect={false}
              // Gemide tek gemi: salt okunur ama soluk değil (plan §0.6; G3-16)
              {...(source.scope === 'ship' ? { readOnly: true, rightSection: null } : {})}
              onChange={(value) => value && selectVessel(value)}
              mb={10}
            />
            <TextInput
              placeholder={t('restHours.searchCrew')}
              aria-label={t('restHours.searchCrew')}
              leftSection={<IconSearch size={14} aria-hidden />}
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              mb={6}
            />
            {loadingCrew ? (
              <div className={classes.peopleSkeleton} aria-hidden>
                {Array.from({ length: 6 }, (_, n) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: sabit iskelet satırları
                  <Skeleton key={n} height={38} />
                ))}
              </div>
            ) : visibleCrew.length === 0 ? (
              <EmptyState
                className={classes.compactEmpty}
                icon={<IconUserSearch size={20} stroke={1.8} />}
                title={query ? t('restHours.noCrewMatch') : t('restHours.noCrew')}
              />
            ) : (
              <ul className={classes.people}>
                {visibleCrew.map((p) => {
                  const active = p.userId === person?.userId;
                  const role = rankOf(p.userId);
                  return (
                    <li key={p.userId}>
                      <UnstyledButton
                        className={classes.person}
                        data-active={active || undefined}
                        aria-pressed={active}
                        onClick={() => setPersonId(p.userId)}
                      >
                        <PersonAvatar name={p.fullName} size={34} />
                        <span className={classes.personText}>
                          <span className={classes.personName} title={p.fullName}>
                            {p.fullName}
                          </span>
                          <span className={classes.personRole} title={role ?? undefined}>
                            {role ?? EMPTY}
                          </span>
                        </span>
                        {statusOf(p.userId) === 'on_board' ? (
                          <span className={classes.onBoard}>
                            <span className={classes.dot} aria-hidden />
                            {/* Dar listede yalnız nokta; yazı ekran okuyucuda kalır (G3-05) */}
                            <span className={classes.onBoardText}>{t('crew.status.on_board')}</span>
                          </span>
                        ) : null}
                        <IconChevronRight size={12} className={classes.chevron} aria-hidden />
                      </UnstyledButton>
                    </li>
                  );
                })}
              </ul>
            )}
          </Panel>

          <div className={classes.center}>
            <Panel flush>
              {person ? (
                <div className={classes.personHead}>
                  <PersonAvatar name={person.fullName} size={compact ? 56 : 72} />
                  <div className={classes.personHeadText}>
                    <div className={classes.nameRow}>
                      <h2 className={classes.personTitle}>{person.fullName}</h2>
                      {personStatus ? (
                        <StatusBadge
                          tone={personStatus === 'on_board' ? 'success' : 'neutral'}
                          outlined
                        >
                          {t(`crew.status.${personStatus}`)}
                        </StatusBadge>
                      ) : null}
                    </div>
                    <span className={classes.personSub}>{member?.role ?? EMPTY}</span>
                    <span className={classes.personVessel}>{activeVessel?.name}</span>
                  </div>
                  <dl className={classes.headStats}>
                    <div className={classes.headStat}>
                      <dt>{t('restHours.today')}</dt>
                      <dd className={classes.todayRows}>
                        <span>
                          <IconBriefcase size={13} aria-hidden /> {t('restHours.work')}
                        </span>
                        <span className={classes.headValue}>
                          {today24 ? formatHours(today24.workHours) : EMPTY}
                        </span>
                        <span>
                          <IconBed size={13} aria-hidden /> {t('restHours.rest')}
                        </span>
                        <span className={classes.headValue}>
                          {today24 ? formatHours(today24.restHours) : EMPTY}
                        </span>
                      </dd>
                    </div>
                    <div className={classes.headStat}>
                      <dt>{t('restHours.mlcStatus')}</dt>
                      <dd>{today24 ? statusBadge(today24.status) : EMPTY}</dd>
                    </div>
                    <div className={classes.headStat}>
                      <dt>{t('restHours.rest24h')}</dt>
                      <dd>
                        <strong className={classes.headValue}>
                          {ring === null ? EMPTY : formatHours(ring)}
                        </strong>{' '}
                        / {formatHours(MLC_LIMITS.minRest24h)}
                        <small>{t('restHours.requiredShort')}</small>
                      </dd>
                    </div>
                    <div className={classes.headStat}>
                      <dt>{t('restHours.rest7d')}</dt>
                      <dd>
                        <strong className={classes.headValue}>
                          {today24?.rest7d == null ? EMPTY : formatHours(today24.rest7d)}
                        </strong>{' '}
                        / {formatHours(MLC_LIMITS.minRest7d)}
                        <small>{t('restHours.requiredShort')}</small>
                      </dd>
                    </div>
                  </dl>
                </div>
              ) : loadingCrew ? (
                <div className={classes.personHead} aria-hidden>
                  <Skeleton circle height={compact ? 56 : 72} />
                  <Skeleton height={14} width={180} />
                </div>
              ) : null}

              <Tabs value={tab} onChange={setTab} className={classes.tabs}>
                <Tabs.List aria-label={t('restHours.tabs.label')}>
                  {/* Dar şeritte (1280–1440 px) kısa ad görünür; erişilebilir ad hep uzun ad, kısa
                      ad onun başı (M6f G1d-3). Şerit kaymaz. */}
                  {TAB_KEYS.map((key) => (
                    <Tabs.Tab key={key} value={key} aria-label={t(`restHours.tabs.${key}`)}>
                      <span className={classes.tabFull}>{t(`restHours.tabs.${key}`)}</span>
                      <span className={classes.tabShort}>{t(`restHours.tabs.short.${key}`)}</span>
                    </Tabs.Tab>
                  ))}
                </Tabs.List>

                <Tabs.Panel value="daily">
                  <div className={classes.toolbar}>
                    <PeriodNav
                      label={day ? formatDayLong(day, locale) : EMPTY}
                      previousLabel={t('restHours.previousDay')}
                      nextLabel={t('restHours.nextDay')}
                      onPrevious={() => move(-1)}
                      onNext={() => move(1)}
                      previousDisabled={!day}
                      nextDisabled={!day || !today || day >= today}
                    />
                    {span && !plainDay && !span.skipped ? (
                      // Saat değişen günün süresi (ADR 0065 §7): 23 / 25 saatlik gün
                      <span className={classes.dayBadge}>
                        {t('restHours.clock.dayLength', {
                          hours:
                            span.minutes % 60 === 0 ? span.minutes / 60 : clockText(span.minutes),
                        })}
                      </span>
                    ) : null}
                    {/* Görünüm tek seçenekli (24 saat): dar kartta gizlenir */}
                    <span className={classes.viewGroup}>
                      <span className={classes.toolbarDivider} aria-hidden />
                      <span className={classes.viewLabel}>{t('restHours.view')}</span>
                      <Select
                        aria-label={t('restHours.view')}
                        data={[{ value: '24', label: t('restHours.view24') }]}
                        value="24"
                        allowDeselect={false}
                        w={150}
                      />
                    </span>
                  </div>

                  {clockNoticeList.map((change) => (
                    <div key={`clock-${change.id}`} className={classes.clockNotice}>
                      <RestNotice
                        tone={change.status === 'pending' ? 'warning' : 'info'}
                        icon={
                          change.status === 'pending' ? (
                            <IconAlertTriangle size={18} />
                          ) : (
                            <IconInfoCircleFilled size={18} />
                          )
                        }
                        role="status"
                      >
                        {clockNoticeText(change)}
                      </RestNotice>
                    </div>
                  ))}
                  <div className={classes.timeline}>
                    {span?.skipped ? (
                      <RestNotice tone="info" icon={<IconInfoCircleFilled size={18} />}>
                        {t('restHours.clock.dateSkipped')}
                      </RestNotice>
                    ) : !plainDay && span && axis ? (
                      periods === null ? (
                        <Skeleton height={30} />
                      ) : (
                        <ClockDayBand
                          span={span}
                          axis={axis}
                          segments={segments}
                          nowMinute={nowMinute}
                          label={boundary}
                          activityLabel={activityLabel}
                          formatDuration={formatMinutes}
                        />
                      )
                    ) : (
                      <>
                        <div className={classes.hours} aria-hidden>
                          {Array.from({ length: 13 }, (_, i) => i * 120).map((minute, i) => (
                            <span
                              key={minute}
                              data-edge={i === 0 ? 'start' : i === 12 ? 'end' : undefined}
                              style={{ left: `${(i / 12) * 100}%` }}
                            >
                              {clock(minute)}
                            </span>
                          ))}
                        </div>
                        {periods === null ? (
                          <Skeleton height={30} />
                        ) : (
                          <div className={classes.bandWrap}>
                            <ol
                              className={classes.band}
                              aria-label={t('restHours.timeline', {
                                date: day ? formatDay(day) : '',
                              })}
                            >
                              {segments.map((segment) => (
                                <li
                                  key={`${segment.from}-${segment.to}`}
                                  className={classes.segment}
                                  data-kind={segment.kind}
                                  data-short={segment.short || undefined}
                                  style={{
                                    flexGrow: segment.to - segment.from,
                                    background: colorOf(
                                      segment.kind === 'work' ? segment.activity : null,
                                    ),
                                  }}
                                >
                                  <span className={classes.visuallyHidden}>
                                    {t('restHours.segment', {
                                      kind: activityLabel(
                                        segment.kind === 'work' ? segment.activity : null,
                                      ),
                                      from: clock(segment.from),
                                      to: clock(segment.to),
                                      duration: formatMinutes(segment.to - segment.from),
                                    })}
                                  </span>
                                </li>
                              ))}
                            </ol>
                            {nowMinute !== null && nowMinute >= 0 && nowMinute <= dayLength ? (
                              <span
                                className={classes.now}
                                style={{ left: `${(nowMinute / dayLength) * 100}%` }}
                                title={t('restHours.now')}
                                aria-hidden
                              />
                            ) : null}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <section
                    className={`${restTable.frame} ${classes.dailyFrame}`}
                    aria-label={t('restHours.periodsTable')}
                    // Yatay kayan tablo klavyeyle de kaydırılabilsin (WCAG 2.1.1)
                    // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge
                    tabIndex={0}
                  >
                    <table className={restTable.table}>
                      <thead>
                        <tr>
                          <th scope="col">{t('restHours.columns.start')}</th>
                          <th scope="col">{t('restHours.columns.end')}</th>
                          <th scope="col">{t('restHours.columns.duration')}</th>
                          <th scope="col">{t('restHours.columns.activity')}</th>
                          <th scope="col">{t('restHours.columns.location')}</th>
                          <th scope="col">{t('restHours.columns.note')}</th>
                        </tr>
                      </thead>
                      <tbody>{dailyRows}</tbody>
                    </table>
                    <div className={classes.frameFoot}>
                      {source.editor && person ? (
                        <Button
                          ref={editButton}
                          variant="subtle"
                          size="compact-sm"
                          fw={400}
                          leftSection={<IconPencil size={15} />}
                          onClick={() => {
                            // Pencere açıkken kişi sabit: varsayılan seçim de açık seçim olur
                            setPersonId(person.userId);
                            setEditing(true);
                          }}
                        >
                          {t('restHours.edit.open')}
                        </Button>
                      ) : (
                        <p className={classes.readOnly}>{t('restHours.readOnly')}</p>
                      )}
                    </div>
                  </section>
                </Tabs.Panel>

                <Tabs.Panel value="weekly">
                  <section
                    className={`${restTable.frame} ${classes.dailyFrame}`}
                    aria-label={t('restHours.weeklyTable')}
                    // Yatay kayan tablo klavyeyle de kaydırılabilsin (WCAG 2.1.1)
                    // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge
                    tabIndex={0}
                  >
                    <table className={restTable.table}>
                      <thead>
                        <tr>
                          <th scope="col">{t('restHours.columns.date')}</th>
                          <th scope="col">{t('restHours.columns.work')}</th>
                          <th scope="col">{t('restHours.columns.rest')}</th>
                          <th scope="col">{t('restHours.columns.rest24h')}</th>
                          <th scope="col">{t('restHours.columns.rest7d')}</th>
                          <th scope="col">{t('restHours.columns.status')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {weekDays.length === 0 ? (
                          <tr>
                            <td colSpan={6} className={restTable.secondary}>
                              {loadingCrew ? (
                                <Skeleton height={10} width={200} />
                              ) : (
                                t('restHours.logsEmpty')
                              )}
                            </td>
                          </tr>
                        ) : (
                          [...weekDays].reverse().map((d) => (
                            <tr key={d.date}>
                              <td className={restTable.num}>{formatDay(d.date)}</td>
                              <td className={restTable.num}>{formatHours(d.workHours)}</td>
                              <td className={restTable.num}>{formatHours(d.restHours)}</td>
                              <td className={restTable.num}>
                                {d.rest24h === null ? EMPTY : formatHours(d.rest24h)}
                              </td>
                              <td className={restTable.num}>
                                {d.rest7d === null ? EMPTY : formatHours(d.rest7d)}
                              </td>
                              <td>{statusBadge(d.status)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </section>
                </Tabs.Panel>
                <Tabs.Panel value="monthly">
                  {person && activeVessel && today && shownMonth ? (
                    <RestMonthView
                      key={`${activeVessel.id}-${person.userId}`}
                      source={source}
                      vesselId={activeVessel.id}
                      timeZone={shipTime}
                      vessel={activeVessel}
                      person={{
                        userId: person.userId,
                        fullName: person.fullName,
                        rank: member?.role ?? null,
                      }}
                      month={shownMonth}
                      onMonthChange={setMonth}
                      today={today}
                      nameOf={(userId) =>
                        people.get(userId)?.fullName ??
                        crew.find((c) => c.userId === userId)?.fullName ??
                        EMPTY
                      }
                    />
                  ) : loadingCrew ? (
                    <Skeleton height={320} m="md" />
                  ) : (
                    <EmptyState
                      icon={<IconUserSearch size={22} stroke={1.8} />}
                      title={t('restHours.choosePerson')}
                    />
                  )}
                </Tabs.Panel>
                <Tabs.Panel value="certificates">
                  <EmptyState
                    icon={<IconCertificate size={22} stroke={1.8} />}
                    title={t('restHours.certificatesTitle')}
                    description={
                      shell && person ? (
                        <Anchor
                          fz="inherit"
                          href={`/crew/certificates?user=${encodeURIComponent(person.userId)}`}
                          onClick={(event) => {
                            event.preventDefault();
                            shell.navigate(
                              `/crew/certificates?user=${encodeURIComponent(person.userId)}`,
                            );
                          }}
                        >
                          {t('restHours.certificatesLink', { name: person.fullName })}
                        </Anchor>
                      ) : (
                        t('restHours.certificatesText')
                      )
                    }
                  />
                </Tabs.Panel>
                <Tabs.Panel value="notes">
                  <EmptyState
                    icon={<IconNotes size={22} stroke={1.8} />}
                    title={t('restHours.notesEmpty')}
                  />
                </Tabs.Panel>
              </Tabs>
            </Panel>

            <div className={classes.bottom}>
              <Panel
                title={t('restHours.weekChart')}
                className={classes.chartPanel}
                extra={
                  <span className={classes.legend}>
                    <span>
                      <i style={{ background: TOKENS['primary-600'] }} aria-hidden />
                      {t('restHours.work')}
                    </span>
                    <span>
                      <i style={{ background: TOKENS['activity-rest'] }} aria-hidden />
                      {t('restHours.rest')}
                    </span>
                  </span>
                }
              >
                <div className={classes.chart}>
                  <div className={classes.axis} aria-hidden>
                    {[0, 8, 16, 24].map((hour) => (
                      <span key={hour} style={{ bottom: `${(hour / 24) * 100}%` }}>
                        {`${hour}h`}
                      </span>
                    ))}
                  </div>
                  <div className={classes.plot}>
                    {[8, 16, 24].map((hour) => (
                      <span
                        key={hour}
                        className={classes.gridLine}
                        style={{ bottom: `${(hour / 24) * 100}%` }}
                        aria-hidden
                      />
                    ))}
                    <ul className={classes.bars}>
                      {weekDays.map((d) => (
                        <li
                          key={d.date}
                          className={classes.barColumn}
                          aria-label={t('restHours.weekBar', {
                            date: formatDay(d.date),
                            work: formatHours(d.workHours),
                            rest: formatHours(d.restHours),
                          })}
                        >
                          <span className={classes.bar}>
                            <span
                              className={classes.barRest}
                              style={{ height: `${(d.restHours / 24) * 100}%` }}
                            />
                            <span
                              className={classes.barWork}
                              style={{ height: `${(d.workHours / 24) * 100}%` }}
                            />
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <span />
                  <ul className={classes.barLabels} aria-hidden>
                    {weekDays.map((d) => (
                      <li key={d.date}>
                        <span>{d.date.slice(8, 10)}</span>
                        <span>{monthName.format(asDate(d.date))}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Panel>

              <Panel title={t('restHours.distribution')} className={classes.distPanel}>
                <div className={classes.distWrap}>
                  <div className={classes.distribution}>
                    <RingProgress
                      size={108}
                      thickness={11}
                      aria-label={t('restHours.distribution')}
                      sections={
                        totalWork === 0
                          ? [{ value: 100, color: TOKENS['page-bg'] }]
                          : totals.map((item) => ({
                              value: item.share * 100,
                              color: colorOf(item.activity),
                            }))
                      }
                      label={
                        <div className={classes.ringLabel}>
                          <strong>{`${Math.round(totalWork / 60)}h`}</strong>
                          <span>{t('restHours.totalWork')}</span>
                        </div>
                      }
                    />
                    {totals.length === 0 ? (
                      <p className={classes.muted}>{t('restHours.noWork')}</p>
                    ) : (
                      <ul className={classes.types}>
                        {totals.map((item) => (
                          <li key={item.activity}>
                            <span className={classes.typeName}>
                              <ActivityDot activity={item.activity} />
                              <span>{activityLabel(item.activity)}</span>
                            </span>
                            <span className={restTable.num}>{formatClock(item.minutes)}</span>
                            <span className={classes.share}>{Math.round(item.share * 100)}%</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </Panel>

              <Panel title={t('restHours.logs')} className={classes.logsPanel}>
                <RecentLogs
                  periods={periods}
                  label={activityLabel}
                  timeZone={shipTime}
                  error={periodsError}
                />
              </Panel>
            </div>
          </div>
        </div>
      </div>

      <aside className={classes.side}>
        <Panel className={classes.dateCard} aria-label={t('restHours.dateCard')}>
          <div className={classes.dateCardRow}>
            <DayInput
              className={classes.datePicker}
              variant="unstyled"
              aria-label={t('restHours.date')}
              leftSection={<IconCalendar size={20} aria-hidden />}
              value={day}
              maxDate={today ?? undefined}
              onChange={(value) => value && changeDay(value)}
            />
            <ActionIcon
              variant="subtle"
              color="navy"
              size={32}
              aria-label={t('restHours.previousDay')}
              onClick={() => move(-1)}
              disabled={!day}
            >
              <IconChevronLeft size={18} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="navy"
              size={32}
              aria-label={t('restHours.nextDay')}
              disabled={!day || !today || day >= today}
              onClick={() => move(1)}
            >
              <IconChevronRight size={18} />
            </ActionIcon>
          </div>
        </Panel>

        <Panel title={t('restHours.limits')} className={classes.limitsPanel}>
          <dl className={classes.limits}>
            {(['minRest24h', 'maxWork24h', 'minRest7d', 'maxWork7d'] as const).map((key) => (
              <div key={key}>
                <dt>{t(`restHours.limit.${key}`)}</dt>
                <dd>{t('restHours.hoursValue', { hours: MLC_LIMITS[key] })}</dd>
              </div>
            ))}
          </dl>
          <Popover width={300} position="bottom-start" withinPortal>
            <Popover.Target>
              <UnstyledButton className={classes.link}>{t('restHours.rulesLink')}</UnstyledButton>
            </Popover.Target>
            <Popover.Dropdown className={classes.rules}>
              {t('restHours.rulesText')}
            </Popover.Dropdown>
          </Popover>
        </Panel>

        <Panel
          title={t('restHours.compliance')}
          className={`${classes.compliancePanel} ${classes.grow}`}
          extra={
            today24 ? (
              <StatusBadge tone={DAY_TONE[today24.status]} withIcon outlined>
                {t(`restHours.status.${today24.status}`)}
              </StatusBadge>
            ) : null
          }
        >
          <div className={classes.compliance}>
            <RingProgress
              size={narrowSide ? 112 : 132}
              thickness={narrowSide ? 12 : 13}
              roundCaps
              rootColor={ring !== null && !compliant ? TOKENS['danger-bg'] : TOKENS['success-bg']}
              aria-label={t('restHours.restIn24h')}
              sections={[
                {
                  value: ring === null ? 0 : Math.min(100, (ring / 24) * 100),
                  color: ring !== null && !compliant ? TOKENS['danger-fg'] : TOKENS['success-fg'],
                },
              ]}
              label={
                <div className={classes.ringLabel} data-size="large">
                  <strong>{ring === null ? EMPTY : formatHours(ring)}</strong>
                  <span>{t('restHours.restIn24h')}</span>
                </div>
              }
            />
            <dl className={classes.ringFacts}>
              <div>
                <dt>{t('restHours.required')}</dt>
                <dd>{formatHours(MLC_LIMITS.minRest24h)}</dd>
              </div>
              <div>
                <dt>{t('restHours.remaining')}</dt>
                <dd data-ok={compliant || undefined}>
                  {ring === null ? EMPTY : formatHours(Math.max(0, ring - MLC_LIMITS.minRest24h))}
                </dd>
              </div>
            </dl>
          </div>

          <h3 className={classes.subTitle}>{t('restHours.weekSummary')}</h3>
          <div className={classes.week}>
            <div className={classes.weekRow}>
              <span>
                {t('restHours.rest')}{' '}
                <strong>{today24?.rest7d == null ? EMPTY : formatHours(today24.rest7d)}</strong>
              </span>
              <span>
                {t('restHours.required')} <strong>{formatHours(MLC_LIMITS.minRest7d)}</strong>
              </span>
            </div>
            <div className={classes.meter} aria-hidden>
              <span
                className={classes.meterFill}
                data-low={
                  today24?.rest7d != null && today24.rest7d < MLC_LIMITS.minRest7d
                    ? true
                    : undefined
                }
                style={{ width: `${meter.fill}%` }}
              />
              <span className={classes.meterMark} style={{ left: `${meter.mark}%` }} />
            </div>
          </div>

          <h3 className={classes.subTitle}>{t('restHours.restPeriods')}</h3>
          {periods === null && !periodsError ? (
            <Skeleton height={60} mb={14} />
          ) : restParts.length === 0 ? (
            <p className={`${classes.muted} ${classes.restEmpty}`}>
              {t('restHours.noRestPeriods')}
            </p>
          ) : (
            <ul className={classes.restList}>
              {restParts.map((segment) => (
                <li key={`${segment.from}-${segment.to}`}>
                  <IconCircleCheckFilled size={18} className={classes.check} aria-hidden />
                  <span className={restTable.num}>
                    {clock(segment.from)} – {clock(segment.to === dayLength ? 1440 : segment.to)}
                  </span>
                  <strong className={restTable.num}>
                    {formatMinutes(segment.to - segment.from)}
                  </strong>
                </li>
              ))}
            </ul>
          )}

          <div className={classes.notice} data-tone={notice === 'clear' ? 'info' : 'warning'}>
            <IconInfoCircleFilled size={20} className={classes.noticeIcon} aria-hidden />
            {notice === 'clear' ? (
              <div>
                <p>{t('restHours.noFindings')}</p>
                <p className={classes.noticeSub}>{t('restHours.noViolations')}</p>
              </div>
            ) : notice === 'incomplete' ? (
              <div>
                <p>{t('restHours.incompleteTitle')}</p>
                <p className={classes.noticeSub}>{t('restHours.incompleteText')}</p>
              </div>
            ) : (
              <ul className={classes.findings}>
                {findings.map((finding) => (
                  <li key={`${finding.rule}-${finding.from}`}>
                    {t(`restHours.rule.${finding.rule}`, {
                      value: Math.round(finding.value * 100) / 100,
                      limit: finding.limit,
                    })}
                    {finding.exception ? (
                      <small className={classes.noticeSub}> {t('restHours.exception')}</small>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Panel>
      </aside>
      {source.editor && person && day && today ? (
        <RestDayEditor
          opened={editing}
          onClose={() => {
            setEditing(false);
            // Odak pencereyi açan düğmeye döner (G3-31)
            requestAnimationFrame(() => editButton.current?.focus());
          }}
          editor={source.editor}
          userId={person.userId}
          fullName={person.fullName}
          rank={member?.role ?? null}
          day={day}
          today={today}
          onDayChange={changeDay}
          timeZone={shipTime}
          pendingClock={(clockChanges ?? []).filter((c) => c.status === 'pending')}
          periods={periods}
          error={periodsError}
          onRetry={() => setRevision((n) => n + 1)}
          month={monthRecord}
          onChanged={() => setRevision((n) => n + 1)}
        />
      ) : null}
    </div>
  );
}

/** Son kayıtlar: kişinin son 7 gündeki dönemlerinin başlangıç ve bitişleri (en yeni önce). */
function RecentLogs({
  periods,
  label,
  timeZone,
  error,
}: {
  periods: readonly WorkPeriod[] | null;
  label: (activity: string | null) => string;
  timeZone: ShipTime;
  error: string | null;
}) {
  const { t } = useTranslation();
  if (periods === null) {
    return error ? (
      <p className={classes.muted}>{t('restHours.periodsError')}</p>
    ) : (
      <div className={classes.peopleSkeleton} aria-hidden>
        {[0, 1, 2, 3].map((n) => (
          <Skeleton key={n} height={28} />
        ))}
      </div>
    );
  }
  const events = periods
    .flatMap((p) => [
      {
        at: p.start,
        text: t('restHours.logStart', { activity: label(p.activity) }),
        kind: 'start',
      },
      { at: p.end, text: t('restHours.logEnd', { activity: label(p.activity) }), kind: 'end' },
    ])
    .sort((x, y) => y.at.localeCompare(x.at))
    .slice(0, 4);
  if (events.length === 0) return <p className={classes.muted}>{t('restHours.logsEmpty')}</p>;
  return (
    <ul className={classes.logs}>
      {events.map((event) => (
        <li key={`${event.at}-${event.kind}`}>
          <span className={classes.logDot} data-kind={event.kind} aria-hidden />
          <span className={classes.logBody}>
            <span className={classes.logTime}>
              {formatDateTime(event.at, offsetAt(timeZone, Date.parse(event.at)))}
            </span>
            <span className={classes.logText} title={event.text}>
              {event.text}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}
